/**
 * PromptVersionControl - Version control system for prompts
 * 
 * Provides:
 * - Version numbering (semantic versioning)
 * - Change history tracking
 * - Diff generation
 * - Rollback capabilities
 * - Export/Import versions
 * 
 * @module services/PromptVersionControl
 */

import StorageService from './StorageService.js';
import EventBus from '../core/EventBus.js';

class PromptVersionControl {
  constructor() {
    this.storageKey = 'promptVersions';
    this.versions = this.loadVersions();
    this.currentVersions = {};
  }
  
  /**
   * Initialize version control
   */
  initialize() {
    // Load existing versions
    this.versions = this.loadVersions();

    // Load current versions if available; otherwise derive from history
    const storedCurrent = StorageService.get('promptCurrentVersions', {});
    if (storedCurrent && Object.keys(storedCurrent).length > 0) {
      this.currentVersions = storedCurrent;
    } else {
      this.currentVersions = {};
      Object.keys(this.versions).forEach(key => {
        const hist = this.versions[key];
        if (Array.isArray(hist) && hist.length > 0) {
          this.currentVersions[key] = hist[hist.length - 1].version;
        }
      });
      // Fallback defaults for any missing keys
      this.initializeVersionNumbers();
    }
    
    console.log('📚 Prompt Version Control initialized');
  }
  
  /**
   * Initialize version numbers for prompts
   */
  initializeVersionNumbers() {
    const promptKeys = ['strategyFileUpload', 'outcomeAssistant', 'emergentStrategy', 'riskAnalysis', 'portfolioAnalysis', 'initiativeTransfer'];
    
    promptKeys.forEach(key => {
      if (!this.currentVersions[key]) {
        this.currentVersions[key] = '1.0.0';
      }
    });
  }
  
  /**
   * Save a new version of a prompt
   */
  saveVersion(promptKey, promptContent, metadata = {}) {
    if (!this.versions[promptKey]) {
      this.versions[promptKey] = [];
    }
    
    // Increment version number
    const currentVersion = this.currentVersions[promptKey] || '1.0.0';
    const newVersion = this.incrementVersion(currentVersion, metadata.changeType || 'patch');
    
    // Create version entry
    const versionEntry = {
      version: newVersion,
      content: promptContent,
      timestamp: new Date().toISOString(),
      author: metadata.author || 'User',
      changeDescription: metadata.description || 'Updated prompt',
      changeType: metadata.changeType || 'patch', // major, minor, patch
      hash: this.generateHash(promptContent),
      previousVersion: currentVersion,
      diff: this.generateDiff(this.getVersion(promptKey, currentVersion)?.content, promptContent)
    };
    
    // Add to version history
    this.versions[promptKey].push(versionEntry);
    
    // Keep only last 10 versions per prompt
    if (this.versions[promptKey].length > 10) {
      this.versions[promptKey].shift();
    }
    
    // Update current version
    this.currentVersions[promptKey] = newVersion;
    
    // Save to storage
    this.saveVersions();
    
    // Emit event
    EventBus.emit('prompt:version-saved', {
      promptKey,
      version: newVersion,
      entry: versionEntry
    });
    
    return newVersion;
  }
  
  /**
   * Increment version number based on change type
   */
  incrementVersion(version, changeType = 'patch') {
    const [major, minor, patch] = version.split('.').map(Number);
    
    switch (changeType) {
      case 'major':
        return `${major + 1}.0.0`;
      case 'minor':
        return `${major}.${minor + 1}.0`;
      case 'patch':
      default:
        return `${major}.${minor}.${patch + 1}`;
    }
  }
  
  /**
   * Get a specific version of a prompt
   */
  getVersion(promptKey, version) {
    if (!this.versions[promptKey]) return null;
    
    return this.versions[promptKey].find(v => v.version === version);
  }
  
  /**
   * Get version history for a prompt
   */
  getHistory(promptKey, limit = 10) {
    if (!this.versions[promptKey]) return [];
    
    return this.versions[promptKey]
      .slice(-limit)
      .reverse(); // Most recent first
  }
  
  /**
   * Get current version number for a prompt
   */
  getCurrentVersion(promptKey) {
    return this.currentVersions[promptKey] || '1.0.0';
  }
  
  /**
   * Rollback to a specific version
   */
  rollback(promptKey, targetVersion) {
    const versionEntry = this.getVersion(promptKey, targetVersion);
    
    if (!versionEntry) {
      throw new Error(`Version ${targetVersion} not found for prompt ${promptKey}`);
    }
    
    // Create a rollback entry
    this.saveVersion(promptKey, versionEntry.content, {
      description: `Rolled back to version ${targetVersion}`,
      changeType: 'patch',
      author: 'System'
    });
    
    return versionEntry.content;
  }
  
  /**
   * Generate a simple diff between two texts
   */
  generateDiff(oldText, newText) {
    if (!oldText || !newText) return null;
    
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    
    const diff = {
      added: 0,
      removed: 0,
      modified: 0,
      changes: []
    };
    
    // Simple line-by-line comparison
    const maxLines = Math.max(oldLines.length, newLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const oldLine = oldLines[i];
      const newLine = newLines[i];
      
      if (oldLine === undefined && newLine !== undefined) {
        diff.added++;
        diff.changes.push({ type: 'add', line: i, content: newLine });
      } else if (oldLine !== undefined && newLine === undefined) {
        diff.removed++;
        diff.changes.push({ type: 'remove', line: i, content: oldLine });
      } else if (oldLine !== newLine) {
        diff.modified++;
        diff.changes.push({ type: 'modify', line: i, old: oldLine, new: newLine });
      }
    }
    
    return diff;
  }
  
  /**
   * Generate a hash for content (simple implementation)
   */
  generateHash(content) {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }
  
  /**
   * Compare two versions
   */
  compareVersions(promptKey, version1, version2) {
    const v1 = this.getVersion(promptKey, version1);
    const v2 = this.getVersion(promptKey, version2);
    
    if (!v1 || !v2) {
      throw new Error('One or both versions not found');
    }
    
    return {
      version1: v1,
      version2: v2,
      diff: this.generateDiff(v1.content, v2.content),
      timeDiff: new Date(v2.timestamp) - new Date(v1.timestamp)
    };
  }
  
  /**
   * Export version history
   */
  exportHistory(promptKey = null) {
    const data = promptKey 
      ? { [promptKey]: this.versions[promptKey] || [] }
      : this.versions;
    
    const exportData = {
      exported: new Date().toISOString(),
      versions: data,
      currentVersions: this.currentVersions
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-versions-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    return exportData;
  }
  
  /**
   * Import version history
   */
  importHistory(data) {
    try {
      const imported = typeof data === 'string' ? JSON.parse(data) : data;
      
      if (imported.versions) {
        Object.entries(imported.versions).forEach(([key, versions]) => {
          if (!this.versions[key]) {
            this.versions[key] = [];
          }
          this.versions[key].push(...versions);
        });
      }
      
      if (imported.currentVersions) {
        Object.assign(this.currentVersions, imported.currentVersions);
      }
      
      this.saveVersions();
      return true;
    } catch (error) {
      console.error('Failed to import version history:', error);
      return false;
    }
  }
  
  /**
   * Get statistics for a prompt
   */
  getStatistics(promptKey) {
    const history = this.getHistory(promptKey);
    
    if (history.length === 0) {
      return {
        totalVersions: 0,
        currentVersion: '1.0.0',
        lastModified: null,
        changeFrequency: 'Never'
      };
    }
    
    const firstVersion = history[history.length - 1];
    const lastVersion = history[0];
    const timeSpan = new Date(lastVersion.timestamp) - new Date(firstVersion.timestamp);
    const days = timeSpan / (1000 * 60 * 60 * 24);
    
    return {
      totalVersions: history.length,
      currentVersion: this.currentVersions[promptKey] || '1.0.0',
      lastModified: lastVersion.timestamp,
      lastAuthor: lastVersion.author,
      majorChanges: history.filter(v => v.changeType === 'major').length,
      minorChanges: history.filter(v => v.changeType === 'minor').length,
      patches: history.filter(v => v.changeType === 'patch').length,
      changeFrequency: days > 0 ? `${(history.length / days).toFixed(1)} changes/day` : 'New',
      totalAdditions: history.reduce((sum, v) => sum + (v.diff?.added || 0), 0),
      totalDeletions: history.reduce((sum, v) => sum + (v.diff?.removed || 0), 0)
    };
  }
  
  /**
   * Load versions from storage
   */
  loadVersions() {
    return StorageService.get('promptVersions', {});
  }
  
  /**
   * Save versions to storage
   */
  saveVersions() {
    StorageService.set('promptVersions', this.versions);
    StorageService.set('promptCurrentVersions', this.currentVersions);
  }
  
  /**
   * Clear all version history
   */
  clearHistory(promptKey = null) {
    if (promptKey) {
      delete this.versions[promptKey];
      this.currentVersions[promptKey] = '1.0.0';
    } else {
      this.versions = {};
      this.initializeVersionNumbers();
    }
    
    this.saveVersions();
  }
}

// Create singleton instance
const promptVersionControl = new PromptVersionControl();

// Initialize on load
promptVersionControl.initialize();

// Export
export default promptVersionControl;

// Attach to window in development
if (import.meta.env.DEV) {
  window.promptVersionControl = promptVersionControl;
  console.log('📚 Prompt Version Control available at window.promptVersionControl');
}
